import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { API } from '@/App';
import { 
  DollarSign, Search, Calendar, IndianRupee, Download, Receipt, 
  ArrowDownCircle, Filter, ArrowUpDown, Building2, ChevronDown, ChevronRight,
  Folder, FolderOpen, User, Eye, Wallet, CheckCircle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export default function VendorPaymentHistory() {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [totals, setTotals] = useState({ paid: 0, count: 0 });
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [fundLedgers, setFundLedgers] = useState({}); // NEW: Vendor fund ledger per project

  // iter-386u-k — role-aware labels so a vendor sees "My Payments Received"
  // while admin/staff sees "Payments Made to Vendors".
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isVendor = user.role === 'vendor' || user.role === 'labour';
  const labels = isVendor ? {
    title: 'My Payments',
    subtitle: 'Track all payments received against your work',
    totalCardLabel: 'Total Received',
  } : {
    title: 'Vendor Payment Ledger',
    subtitle: 'Track all payments made to vendors',
    totalCardLabel: 'Total Paid',
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      // Get all projects
      const projectsRes = await axios.get(`${API}/projects`);
      setProjects(projectsRes.data);
      
      // Fetch payment ledger and vendor fund ledger for each project
      const allPayments = [];
      const ledgerData = {};
      
      for (const project of projectsRes.data) {
        try {
          const ledgerRes = await axios.get(`${API}/projects/${project.id}/payment-ledger`);
          if (ledgerRes.data.vendor_payments) {
            ledgerRes.data.vendor_payments.forEach(p => {
              if (p.status !== 'cancelled') {
                allPayments.push({
                  ...p,
                  project_id: project.id,
                  project_name: project.name
                });
              }
            });
          }
          
          // Fetch vendor fund ledger
          try {
            const fundRes = await axios.get(`${API}/projects/${project.id}/fund-ledger/vendor-view`);
            if (fundRes.data) {
              ledgerData[project.id] = fundRes.data;
            }
          } catch (e) {
            // Fund ledger not available
          }
        } catch (e) {
          // Skip project if ledger fetch fails
        }
      }
      
      setFundLedgers(ledgerData);
      
      // Sort by date descending
      allPayments.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setPayments(allPayments);
      
      // Calculate totals
      const totalPaid = allPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      setTotals({ paid: totalPaid, count: allPayments.length });
      
      // Projects collapsed by default
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/payments/${paymentId}/receipt-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  // Group payments by project
  const groupedByProject = useMemo(() => {
    const groups = {};
    
    payments.forEach(payment => {
      const matchesSearch = !searchQuery || 
        payment.notes?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase());
      
      if (matchesSearch) {
        if (!groups[payment.project_id]) {
          groups[payment.project_id] = {
            project_id: payment.project_id,
            project_name: payment.project_name,
            payments: [],
            total: 0
          };
        }
        groups[payment.project_id].payments.push(payment);
        groups[payment.project_id].total += payment.amount || 0;
      }
    });
    
    return Object.values(groups);
  }, [payments, searchQuery]);

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedProjects(new Set(projects.map(p => p.id)));
  };

  const collapseAll = () => {
    setExpandedProjects(new Set());
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-900" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-payment-history-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
              <IndianRupee className="text-purple-600" size={24} />
              {labels.title}
            </h1>
            <p className="text-stone-500 mt-1">{labels.subtitle}</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <IndianRupee size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">{labels.totalCardLabel}</p>
                  <p className="text-2xl font-bold text-purple-700">₹{totals.paid.toLocaleString('en-IN')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Receipt size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Transactions</p>
                  <p className="text-2xl font-bold text-blue-700">{totals.count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Building2 size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Active Projects</p>
                  <p className="text-2xl font-bold text-amber-700">{projects.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search by vendor, project, or notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Collapse All
          </Button>
        </div>

        {/* Payments Grouped by Project */}
        <div className="space-y-4">
          {groupedByProject.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <div className="text-center text-stone-500">
                  <Receipt size={40} className="mx-auto mb-3 text-stone-300" />
                  <p>No vendor payments found</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            groupedByProject.map(group => (
              <Collapsible 
                key={group.project_id} 
                open={expandedProjects.has(group.project_id)}
                onOpenChange={() => toggleProject(group.project_id)}
              >
                <Card className="overflow-hidden">
                  {/* Project Header - Clickable to expand/collapse */}
                  <CollapsibleTrigger asChild>
                    <div className="bg-gradient-to-r from-purple-50 to-white px-4 py-3 cursor-pointer hover:bg-purple-100 transition-colors border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedProjects.has(group.project_id) ? (
                            <FolderOpen size={20} className="text-purple-600" />
                          ) : (
                            <Folder size={20} className="text-purple-600" />
                          )}
                          <div>
                            <h3 className="font-semibold text-stone-900">{group.project_name}</h3>
                            <p className="text-xs text-stone-500">{group.payments.length} payment(s)</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-stone-500">{labels.totalCardLabel}</p>
                            <p className="text-lg font-bold text-purple-600">₹{group.total.toLocaleString('en-IN')}</p>
                          </div>
                          {expandedProjects.has(group.project_id) ? (
                            <ChevronDown size={20} className="text-stone-400" />
                          ) : (
                            <ChevronRight size={20} className="text-stone-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  {/* Payments List */}
                  <CollapsibleContent>
                    {/* Vendor Fund Ledger Summary */}
                    {fundLedgers[group.project_id] && (
                      <div className="bg-purple-50/50 p-4 border-b border-purple-100">
                        <div className="flex items-center gap-2 mb-3">
                          <Wallet size={16} className="text-purple-600" />
                          <span className="text-sm font-medium text-purple-700">My Fund Ledger Summary</span>
                          <Badge variant="outline" className="bg-white text-purple-600 border-purple-200 text-xs ml-auto">
                            <Eye size={10} className="mr-0.5" />
                            View Only
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-3">
                          <div className="bg-white p-2 rounded text-center border">
                            <p className="text-xs text-stone-500">My Contract</p>
                            <p className="font-bold text-stone-800">₹{(fundLedgers[group.project_id].vendor_contract_value || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="bg-green-50 p-2 rounded text-center border border-green-100">
                            <p className="text-xs text-green-600">Received</p>
                            <p className="font-bold text-green-700">₹{(fundLedgers[group.project_id].total_received || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="bg-amber-50 p-2 rounded text-center border border-amber-100">
                            <p className="text-xs text-amber-600">Retention</p>
                            <p className="font-bold text-amber-700">₹{(fundLedgers[group.project_id].retention_held || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="bg-blue-50 p-2 rounded text-center border border-blue-100">
                            <p className="text-xs text-blue-600">Pending</p>
                            <p className="font-bold text-blue-700">₹{(fundLedgers[group.project_id].balance_pending || 0).toLocaleString('en-IN')}</p>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-stone-500 mb-1">
                            <span>Payment Progress</span>
                            <span>{fundLedgers[group.project_id].payment_progress_percentage || 0}%</span>
                          </div>
                          <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-purple-500 to-green-500 rounded-full transition-all"
                              style={{ width: `${fundLedgers[group.project_id].payment_progress_percentage || 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="divide-y divide-stone-100">
                      {group.payments.map(payment => (
                        <div key={payment.id} className="p-4 hover:bg-stone-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-full">
                                  <ArrowDownCircle size={16} className="text-purple-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-purple-700">₹{(payment.amount || 0).toLocaleString('en-IN')}</p>
                                  {payment.vendor_name && (
                                    <p className="text-sm text-stone-600 flex items-center gap-1">
                                      <User size={12} />
                                      {payment.vendor_name}
                                    </p>
                                  )}
                                  <p className="text-xs text-stone-400">
                                    {new Date(payment.created_at).toLocaleString('en-IN')}
                                  </p>
                                </div>
                              </div>
                              {payment.notes && (
                                <p className="text-sm text-stone-600 mt-2 ml-11">"{payment.notes}"</p>
                              )}
                              {payment.recorded_by_name && (
                                <p className="text-xs text-stone-400 mt-1 ml-11">
                                  Recorded by: {payment.recorded_by_name}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadReceipt(payment.id)}
                              className="text-purple-600"
                            >
                              <Download size={14} className="mr-1" />
                              Receipt
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
